/**
 * Sandbox Configuration Types
 *
 * Types for ADC sandbox configurations.
 * Sandboxes provide isolated microVM environments where agents can execute
 * code with pre-cloned repositories.
 *
 */

// =============================================================================
// CORE TYPES
// =============================================================================

/**
 * Provisioning state of a sandbox configuration.
 * - InProgress: Snapshot is being built (repos cloned, image created)
 * - Ready: Snapshot built successfully, sandbox can be used
 * - Failed: Snapshot build failed
 */
export const SandboxProvisioningState = {
  InProgress: 'InProgress',
  Ready: 'Ready',
  Failed: 'Failed',
} as const;
export type SandboxProvisioningState = (typeof SandboxProvisioningState)[keyof typeof SandboxProvisioningState];

/**
 * Resource tier for sandbox compute allocation.
 * Controls CPU, memory, and disk budget for the sandbox microVM.
 */
export const ResourceTier = {
  XS: 'XS',
  S: 'S',
  M: 'M',
  L: 'L',
} as const;
export type ResourceTier = (typeof ResourceTier)[keyof typeof ResourceTier];

/** Human-readable descriptions for each resource tier. */
export const ResourceTierLabels: Record<ResourceTier, string> = {
  XS: 'XS — 0.25 cores / 0.5 GB RAM (5 GB disk budget)',
  S: 'S — 0.5 cores / 1 GB RAM (10 GB disk budget)',
  M: 'M — 1 core / 2 GB RAM (20 GB disk budget)',
  L: 'L — 2 cores / 4 GB RAM (40 GB disk budget)',
};

/**
 * Authentication type for repository access.
 */
export const RepositoryAuthType = {
  ManagedIdentity: 'ManagedIdentity',
  PAT: 'PAT',
  OAuth: 'OAuth',
} as const;
export type RepositoryAuthType = (typeof RepositoryAuthType)[keyof typeof RepositoryAuthType];

/**
 * Connector type for OAuth-based repository access.
 * Maps to the APIHub connector registered per automation project.
 */
export const RepoConnectorType = {
  AzureDevOps: 'azuredevops',
  GitHub: 'github',
} as const;
export type RepoConnectorType = (typeof RepoConnectorType)[keyof typeof RepoConnectorType];

/**
 * Agent harness type — the execution engine for the sandbox.
 * Used in agent harness settings (workflow.json).
 */
export const AgentHarnessType = {
  GHCP: 'GHCP',
  None: 'None',
} as const;
export type AgentHarnessType = (typeof AgentHarnessType)[keyof typeof AgentHarnessType];

// =============================================================================
// REPOSITORY TYPES
// =============================================================================

/**
 * A repository to clone into the sandbox.
 */
export interface SandboxRepository {
  url: string;
  branch: string;
  authType: RepositoryAuthType;
  staticToken?: string;
  connectorType?: RepoConnectorType;
  connectorApiName?: string;
  connectionId?: string;
  automationProjectId?: string;
}

/**
 * Repository settings for a sandbox configuration.
 */
export interface RepositorySettings {
  repositories: SandboxRepository[];
}

/**
 * Resource settings for a sandbox configuration.
 */
export interface ResourceSettings {
  resourceTier?: ResourceTier;
}

// =============================================================================
// SANDBOX CONFIGURATION
// =============================================================================

/**
 * Sandbox configuration resource.
 */
export interface SandboxConfiguration {
  id: string;
  name: string;
  type: string;
  properties: SandboxConfigurationProperties;
}

/**
 * Properties of a sandbox configuration.
 */
export interface SandboxConfigurationProperties {
  repositorySettings?: RepositorySettings;
  resourceSettings?: ResourceSettings;
  provisioningState?: SandboxProvisioningState;
  errorMessage?: string;
  createdTime?: string;
  createdBy: {
    objectId: string;
    tenantId: string;
  };
}

// =============================================================================
// AGENT HARNESS SETTINGS (workflow.json)
// =============================================================================

/**
 * An input file to upload to the sandbox before the agent starts.
 * Content can use workflow expressions (e.g., @{body('Get_Blob')}).
 */
export interface AgentHarnessInputFile {
  name: string;
  content: string;
  contentType?: string;
}

/**
 * A skill to load in the agent harness.
 * References a SKILL.md file in a repository that is cloned in the sandbox.
 */
export interface AgentHarnessSkill {
  repository: string;
  folders: string[];
}

/**
 * Agent harness settings stored in workflow.json under
 * agentModelSettings.agentHarness.*
 */
export interface AgentHarnessSettings {
  type: AgentHarnessType;
  sandboxConfigurationName?: string;
  inputFiles?: AgentHarnessInputFile[];
  skills?: AgentHarnessSkill[];
}

// =============================================================================
// API REQUEST/RESPONSE TYPES
// =============================================================================

/**
 * Request body for creating/updating a sandbox configuration (PUT).
 */
export interface PutSandboxConfigurationRequest {
  properties: {
    repositorySettings?: RepositorySettings;
    resourceSettings?: ResourceSettings;
  };
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Check if a sandbox is in a ready state for use.
 */
export function isSandboxReady(sandboxConfig: SandboxConfiguration): boolean {
  return sandboxConfig.properties.provisioningState === SandboxProvisioningState.Ready;
}

export function parseSandboxConfigName(fullName: string): string {
  const parts = fullName.split('/');
  return parts[parts.length - 1] ?? fullName;
}

/**
 * Validate a repository URL for sandbox configuration.
 *
 * Returns a user-facing error message if the URL is invalid, or null if valid.
 * A valid URL must be non-empty and parseable as an absolute http:// or https:// URL.
 */
export function getRepoUrlError(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) {
    return 'Repository URL is required';
  }
  
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      return 'Repository URL must include "https://" or "http://" scheme';
    }
  } catch {
    return 'Repository URL must be a valid URL (e.g. https://dev.azure.com/org/project/_git/repo)';
  }
  
  return null;
}
